import { createTheme, ThemeProvider } from '@mui/material/styles';

declare module '@mui/material/styles' {
    interface TypeText {
        blueish?: string;
    }
}

const theme = createTheme({
    palette: {
        primary: {
            main: "#314c3d",
            light: "#3e5c4b",
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#e6daca',
            contrastText: '#ffffff',
        },
        background: {
            default: "#333333",
            paper: '#ffffff',
        },
        text: {
            primary: '#000000',
            secondary: '#3a3a3a',
            blueish: '#292D39'
        },
        divider: "#3e5c4b",
        success: {
            main: "#3e5c4b",
        }
    },
    typography: {
        fontFamily: '"Montserrat", sans-serif',
        body1: {
            color: '#d0d0d0',
        },
        body2: {
            color: '#d0d0d0',
            fontSize: '0.875rem',
        },
        subtitle2: {
            fontWeight: 500,
            fontSize: '0.875rem',
        },
        caption: {
            color: '#b0b0b0',
        },
    },
    components: {
        // Below here, be careful. The Card MUI component extended from MuiPaper.
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                },
            },
        },
        MuiCard: {
          styleOverrides: {
              root: {
                  borderRadius: 8,
                  border: '1px solid #c0c0c0',
                  color: '#314c3d',
              }
          }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'uppercase',
                    color: '#314c3d',
                    fontWeight: 600,
                    '&.Mui-selected': {
                        color: '#314c3d',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#314c3d',
                },
            },
        },
        MuiIconButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    color: '#fff',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },

                    '&.Mui-focusVisible': {
                        boxShadow: 'none',
                        outline: 'none',
                    },

                    '&:active': {
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    boxShadow: 'none',

                    '&:hover': {
                        boxShadow: 'none',
                    },

                    '&.Mui-focusVisible': {
                        boxShadow: 'none',
                        outline: 'none',
                    },

                    '&:active': {
                        boxShadow: 'none',
                    },
                },

                contained: {
                    backgroundColor: '#3e5c4b',
                    color: '#fff',

                    '&:hover': {
                        backgroundColor: '#314c3d',
                    },
                },

                outlined: {
                    backgroundColor: '#fff',
                    color: '#314c3d',
                    borderColor: '#314c3d',

                    '&:hover': {
                        backgroundColor: 'rgba(49, 76, 61, 0.08)',
                        borderColor: '#314c3d',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    color: '#314c3d',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: '#000',
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                option: {
                    color: '#000',
                },
            },
        },
        // MuiToggleButton: {
        //     styleOverrides: {
        //         root: {
        //             color: '#314c3d',
        //             backgroundColor: '#ffffff',
        //             border: '1px solid #314c3d',
        //             '&.Mui-selected': {
        //                 color: '#ffffff',
        //                 backgroundColor: '#314c3d',
        //                 '&:hover': {
        //                     backgroundColor: '#3e5c4b',
        //                 },
        //             },
        //             '&:hover': {
        //                 backgroundColor: '#314c3d14',
        //             },
        //         },
        //     },
        // },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    color: '#314c3d',
                    backgroundColor: '#ffffff',
                    border: '1px solid #314c3d',
                    // A nem kijelölt gomb bordere alapból is zöld legyen (ne csak hoverkor).
                    // MUI alapból halvány (rgba(0,0,0,.12)) bordert ad a nem kijelölt gombnak,
                    // ezért explicit felülírjuk.
                    borderColor: '#314c3d',
                    '&.Mui-selected': {
                        color: '#ffffff',
                        backgroundColor: '#314c3d',
                        '&:hover': {
                            backgroundColor: '#3e5c4b',
                        },
                    },
                    '&:hover': {
                        backgroundColor: '#314c3d14',
                        borderColor: '#314c3d',
                    },
                },
            },
        },
        // A MUI a szomszédos gombok 1px-es éleit negatív margóval ÁTFEDI, így hovernél
        // (mindkét él zöld + z-index) duplán/vastagon látszik a köztük lévő vonal.
        // Megoldás: a nem-első gombnak ELVESSZÜK a bal borderét, és nincs átfedő margó —
        // így a gombok között MINDIG csak egyetlen él van (a bal szomszéd jobb bordere).
        MuiToggleButtonGroup: {
            styleOverrides: {
                grouped: {
                    '&:not(:first-of-type)': {
                        marginLeft: 0,
                        borderLeft: 'none',
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                    },
                    '&:not(:last-of-type)': {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: '#f6f6f6',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    '&::selection': {
                        backgroundColor: '#c0c0c0',
                        color: '#000',
                    },
                    // Firefox support
                    '&::-moz-selection': {
                        backgroundColor: '#c0c0c0',
                        color: '#000',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: '#000',
                    '&.Mui-focused': {
                        color: '#000',
                    },
                },
            },
        },
    },
});

const UCThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <ThemeProvider theme={theme}>
            {children}
        </ThemeProvider>
    );
}

export default UCThemeProvider;
